// On-demand download of one mission (card #4117).
//
// A Tasker opens a mission that is not saved on the phone and the survey fetch
// fails (weak signal, offline). Instead of a blank page the editor offers
// "Download this mission", and this module does the work: one small
// prefetch.json?response_id= request (a few kilobytes, not the whole sync),
// retried with a longer timeout each time, then kept in a queue so it finishes
// by itself once the signal improves.
//
// Everything a Tasker reads here says "mission", never "visit".
//
// Every attempt is written to the diagnostics log as "Mission download
// attempt": response id, connection level, bytes, duration and outcome.

import db from "./field_db"
import { observedFetch, currentLevel, LEVEL_OFFLINE, LEVEL_DEGRADED, LEVEL_ONLINE } from "./field_network_health"
import { logEvent, logWarn } from "./field_logger"
import { classifySyncResponse, upsertCachedPlace, upsertCachedResponse } from "./field_sync_shared"

// Each retry waits longer for the answer. The single-mission payload is small,
// so the first try is short; a slow link gets 30s and then 60s.
export const ATTEMPT_TIMEOUTS_MS = [15000, 30000, 60000]
// Pause before attempt 2 and attempt 3.
export const BACKOFF_MS = [2000, 5000]

const QUEUE_KEY = "field:mission-download-queue"
const QUEUE_MAX = 200
// The background drain runs at most this often.
const DRAIN_MIN_GAP_MS = 20000

export const MESSAGES = {
  downloading:   "Downloading this mission...",
  retrying:      "Still trying. Your signal looks slow...",
  downloaded:    "Downloaded.",
  weak:          "Your connection isn't strong enough to download this mission right now. We'll keep trying and it will download when your signal improves.",
  offline:       "You're offline. This mission will download when you have a connection.",
  serverError:   "We couldn't reach the server just now. We'll keep trying and this mission will download when it can.",
  reauth:        "Please sign in again to download this mission.",
  notFound:      "This mission isn't on your account any more. Check with Mission Control."
}

export function connectionLabel(level = currentLevel()) {
  if (level === LEVEL_OFFLINE) return "offline"
  if (level === LEVEL_DEGRADED) return "weak"
  return "online"
}

// -- queue (localStorage; small, survives a reload, readable from any page) ----

function readQueue() {
  try {
    const parsed = JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]")
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeQueue(queue) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(0, QUEUE_MAX))) } catch { /* private window */ }
}

export function queuedMissionDownloads() {
  return readQueue()
}

export function isMissionDownloadQueued(responseId) {
  return readQueue().some(item => item.responseId === Number(responseId))
}

export function queueMissionDownload(responseId, { reason = "unknown" } = {}) {
  const id = Number(responseId)
  const queue = readQueue()
  if (queue.some(item => item.responseId === id)) return false
  queue.push({ responseId: id, queuedAt: new Date().toISOString(), reason })
  writeQueue(queue)
  logEvent("sync", "Mission download queued", {
    responseId: id, reason, level: connectionLabel(), queueSize: queue.length
  }).catch(() => {})
  return true
}

export function dequeueMissionDownload(responseId) {
  const id = Number(responseId)
  writeQueue(readQueue().filter(item => item.responseId !== id))
}

// -- one attempt ---------------------------------------------------------------

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function byteLength(text) {
  try { return new Blob([text]).size } catch { return text.length }
}

// One request for one response. Never throws; resolves with
//   { ok, outcome, bytes, durationMs, level, record }
// outcome: "downloaded" | "offline" | "weak_signal" | "server_error" |
//          "reauth" | "not_found"
// record (on success): { response, syncedAt }
export async function attemptMissionDownload(responseId, { attempt = 1, trigger = "button" } = {}) {
  const id = Number(responseId)
  const level = currentLevel()
  const label = connectionLabel(level)
  const timeoutMs = ATTEMPT_TIMEOUTS_MS[Math.min(attempt, ATTEMPT_TIMEOUTS_MS.length) - 1]
  const startedAt = Date.now()
  let outcome, bytes = 0, record = null

  if (level === LEVEL_OFFLINE || navigator.onLine === false) {
    outcome = "offline"
  } else {
    try {
      const res = await observedFetch(
        `/api/v1/field/prefetch.json?response_id=${encodeURIComponent(id)}`,
        {
          method: "GET",
          headers: { "Accept": "application/json" },
          credentials: "include",
          signal: AbortSignal.timeout(timeoutMs)
        },
        { kind: "prefetch", sizeClass: "small" }
      )
      const verdict = await classifySyncResponse(res)
      if (verdict === "reauth-required") {
        outcome = "reauth"
      } else if (verdict === "retry") {
        outcome = "server_error"
      } else if (verdict === "failed") {
        outcome = "not_found"
      } else {
        const text = await res.text()
        bytes = byteLength(text)
        const payload = JSON.parse(text)
        const found = (payload.responses || []).find(r => Number(r.id) === id)
        const hasSurvey = !!(found && found.survey_json && typeof found.survey_json === "object")
        outcome = hasSurvey ? "downloaded" : "not_found"
        record = hasSurvey ? { response: found, syncedAt: payload.synced_at } : null
      }
    } catch {
      // Timed out, dropped, or an unreadable body: the signal could not carry it.
      outcome = "weak_signal"
    }
  }

  const durationMs = Date.now() - startedAt
  logEvent("sync", "Mission download attempt", {
    responseId: id, attempt, trigger, level: label, timeoutMs, bytes, durationMs, outcome
  }).catch(() => {})

  return { ok: outcome === "downloaded", outcome, bytes, durationMs, level: label, record }
}

// Writes a downloaded response into IndexedDB the same way a sync would, then
// tells the rest of the app. The upsert merges any local draft over the
// server's answers (card #3804), so a Tasker already typing is not overwritten.
export async function saveDownloadedMission(record) {
  const r = record.response
  await upsertCachedPlace(db, r.place)
  await upsertCachedResponse(db, r, record.syncedAt || new Date().toISOString())

  // Fire and forget: have the service worker cache this mission's own pages
  // for offline use too.
  const urls = [...(r.warm_urls || []), ...(r.mission?.warm_urls || [])]
  const sw = navigator.serviceWorker?.controller
  if (sw && urls.length > 0) sw.postMessage({ type: "WARM_CACHE", urls })

  dequeueMissionDownload(r.id)
  window.dispatchEvent(new CustomEvent("field:mission-downloaded", { detail: { responseId: r.id } }))
}

function messageFor(outcome) {
  switch (outcome) {
    case "offline":      return MESSAGES.offline
    case "server_error": return MESSAGES.serverError
    case "reauth":       return MESSAGES.reauth
    case "not_found":    return MESSAGES.notFound
    default:             return MESSAGES.weak
  }
}

// The button path. Up to three attempts with a growing timeout and a pause
// between, reporting progress through onState({ state, message, attempt }).
// If it still has not worked it queues the mission and says so.
//
// Final states: "downloaded" | "queued" | "offline" | "reauth" | "not_found"
export async function downloadMissionWithRetry(responseId, { onState = () => {}, trigger = "button" } = {}) {
  const id = Number(responseId)
  let last = null

  for (let attempt = 1; attempt <= ATTEMPT_TIMEOUTS_MS.length; attempt++) {
    onState({ state: "downloading", message: attempt === 1 ? MESSAGES.downloading : MESSAGES.retrying, attempt })
    last = await attemptMissionDownload(id, { attempt, trigger })

    if (last.ok) {
      await saveDownloadedMission(last.record)
      onState({ state: "downloaded", message: MESSAGES.downloaded, attempt })
      return { state: "downloaded", ...last }
    }

    // These will not get better by waiting a few seconds.
    if (last.outcome === "offline" || last.outcome === "reauth" || last.outcome === "not_found") break
    if (attempt < ATTEMPT_TIMEOUTS_MS.length) await sleep(BACKOFF_MS[attempt - 1])
  }

  if (last.outcome === "reauth" || last.outcome === "not_found") {
    logWarn("sync", "Mission download stopped", { responseId: id, outcome: last.outcome, level: last.level }).catch(() => {})
    onState({ state: last.outcome, message: messageFor(last.outcome), attempt: 0 })
    return { state: last.outcome, ...last }
  }

  queueMissionDownload(id, { reason: last.outcome })
  logWarn("sync", "Mission download kept in the queue", {
    responseId: id, outcome: last.outcome, level: last.level
  }).catch(() => {})
  const state = last.outcome === "offline" ? "offline" : "queued"
  onState({ state, message: messageFor(last.outcome), attempt: 0 })
  return { state, ...last }
}

// -- background drain ------------------------------------------------------------

let drainRunning = false
let lastDrainAt = 0

// One attempt per queued mission, longest timeout, only on a healthy
// connection. Called from the always-mounted auto-sync controller on page
// load, when the phone comes back online, and when the app returns to the
// foreground. Single flight per page load.
export async function drainMissionDownloadQueue({ trigger = "auto", force = false } = {}) {
  if (drainRunning) return { ran: false }
  const queue = readQueue()
  if (queue.length === 0) return { ran: false }
  if (currentLevel() !== LEVEL_ONLINE || navigator.onLine === false) return { ran: false }
  if (!force && Date.now() - lastDrainAt < DRAIN_MIN_GAP_MS) return { ran: false }

  drainRunning = true
  lastDrainAt = Date.now()
  let downloaded = 0, failed = 0
  try {
    for (const item of queue) {
      if (currentLevel() !== LEVEL_ONLINE) break
      const result = await attemptMissionDownload(item.responseId, { attempt: ATTEMPT_TIMEOUTS_MS.length, trigger })
      if (result.ok) {
        await saveDownloadedMission(result.record)
        downloaded++
      } else {
        // A mission that is no longer on the account will never download.
        if (result.outcome === "not_found") dequeueMissionDownload(item.responseId)
        failed++
      }
    }
    logEvent("sync", "Mission download queue pass", {
      trigger, queued: queue.length, downloaded, failed, level: connectionLabel()
    }).catch(() => {})
  } finally {
    drainRunning = false
  }
  return { ran: true, downloaded, failed }
}

// -- counts for "N of M missions downloaded" ----------------------------------------

// How many of the missions this phone should hold are actually on it. M is the
// larger of what the last sync said was assigned and what is on the phone, so
// the figure never reads "5 of 3".
export async function missionDownloadCounts() {
  let assigned = 0
  try {
    assigned = Number(JSON.parse(localStorage.getItem("field:sync-summary") || "{}").total) || 0
  } catch { /* none yet */ }

  const rows = await db.cached_responses.toArray()
  const downloaded = rows.filter(r => r.surveyJson && r.status !== "ended").length
  return { downloaded, assigned: Math.max(assigned, downloaded), queued: readQueue().length }
}
